/**
 * Permission-based access control utilities.
 *
 * These replace the legacy role-based helpers in `rolePermissions.ts`.
 * Permission keys mirror the backend catalogue at
 * `services/backend-api/src/permissions/permissions.catalogue.ts`.
 */

// ─── Permission key constants ────────────────────────────────────────────────
// Type-safe references for use across the frontend.

export const P = {
  VIEW_PROJECTS: 'view:projects',
  MANAGE_PROJECTS: 'manage:projects',
  VIEW_PROJECT_VALUE: 'view:project:value',
  VIEW_PROJECT_MARGIN: 'view:project:margin',
  VIEW_RATES_CHARGE: 'view:rates:charge',
  VIEW_RATES_COST: 'view:rates:cost',
  VIEW_SALARY: 'view:salary',
  MANAGE_ALLOCATIONS: 'manage:allocations',
  MANAGE_TIMESHEETS: 'manage:timesheets',
  APPROVE_TIMESHEETS: 'approve:timesheets',
  /**
   * Approve, un-approve or freeze your OWN timesheet.
   *
   * Separate from `APPROVE_TIMESHEETS`, which never covers the holder's own
   * periods: a reviewer approving their own work is a segregation-of-duties
   * exception. OWNER/ADMIN hold it through their presets, so revoking it is what
   * takes the capability away from a specific admin.
   */
  APPROVE_TIMESHEETS_OWN: 'approve:timesheets:own',
  /**
   * Designate (and undesignate) a project's timesheet approvers.
   *
   * Separate from `MANAGE_PROJECTS` on purpose: a designation grants read and
   * approve over the time other people logged to the project, and project
   * editing is far too widely held to carry that. A holder of `MANAGE_PROJECTS` alone sees the
   * approver list and gets no add/remove affordance at all.
   */
  MANAGE_TIMESHEET_APPROVERS: 'manage:timesheets:approvers',
  VIEW_TIMESHEETS_ALL: 'view:timesheets:all',
  MANAGE_USERS: 'manage:users',
  MANAGE_SETTINGS: 'manage:settings',
  VIEW_REPORTS_FINANCIAL: 'view:reports:financial',
  VIEW_REPORTS_OPERATIONAL: 'view:reports:operational',
  MANAGE_METADATA: 'manage:metadata',
  /**
   * Stage writes, including which stages count as delivered revenue — a
   * financial write, which is why it is not one key with `MANAGE_PROJECT_TYPES`.
   */
  MANAGE_PROJECT_STAGES: 'manage:project-stages',
  MANAGE_PROJECT_TYPES: 'manage:project-types',
  MANAGE_RATE_CARDS: 'manage:rate-cards',
  MANAGE_BILLING: 'manage:billing',
  VIEW_AUDIT_LOG: 'view:audit-log',
  USE_AI_ASSISTANT: 'use:ai-assistant',
  MANAGE_AI: 'manage:ai',
  MANAGE_NOTIFICATIONS: 'manage:notifications',
  /**
   * Retention, legal holds, anonymisation and sensitive absence types. The
   * personal-data export also needs `VIEW_SALARY` and `VIEW_SENSITIVE_ABSENCES`,
   * so gate its control on all three.
   */
  MANAGE_PRIVACY: 'manage:privacy',
  /**
   * The true type of a sensitive absence outside the holder's reporting line.
   * Masking is server-side (the client receives "Absence"); the personal-data
   * export needs it beside `MANAGE_PRIVACY` and `VIEW_SALARY`.
   */
  VIEW_SENSITIVE_ABSENCES: 'view:absences:sensitive',
} as const;

export type PermissionKey = (typeof P)[keyof typeof P];

/**
 * Permission keys that grant access to at least one Admin area. Holding **any**
 * one of these lets a user reach the Admin surface; each rail group is then
 * gated per-permission, so a holder only sees the sections they can manage.
 * Every key here must open at least one rail leaf on its own
 * (`adminSurfaceParity.test.ts`), or its holder reaches an empty page.
 *
 * `manage:metadata`, `manage:rate-cards` and `view:rates:charge` are not here:
 * custom fields, detail templates and rate cards live in Projects → Settings.
 *
 * This makes Admin access permission-driven (not role-only), so a custom group
 * granting e.g. `manage:ai` — without an OWNER/ADMIN role — can reach the AI
 * panels it is entitled to. Every panel + endpoint remains permission-gated
 * server-side, so entry never widens what data a user can actually see.
 */
export const ADMIN_SURFACE_PERMISSIONS: string[] = [
  P.MANAGE_USERS,
  P.MANAGE_SETTINGS,
  P.VIEW_RATES_COST,
  P.VIEW_SALARY,
  P.MANAGE_AI,
  P.VIEW_AUDIT_LOG,
  // Without this entry the key would gate the Notifications rail group while
  // admitting nobody to `/admin` to reach it — a holder whose ONLY admin-surface
  // key is `manage:notifications` would be redirected to the dashboard.
  P.MANAGE_NOTIFICATIONS,
  // A delegated privacy administrator may hold no other admin key.
  P.MANAGE_PRIVACY,
];

// ─── Pure utility functions ──────────────────────────────────────────────────

/** Returns true if `permissions` contains the given key. */
export function hasPermission(permissions: string[], key: string): boolean {
  return permissions.includes(key);
}

/** Returns true if `permissions` contains at least one of the given keys. */
export function hasAnyPermission(
  permissions: string[],
  ...keys: string[]
): boolean {
  return keys.some((k) => permissions.includes(k));
}

/** Returns true if `permissions` contains all of the given keys. */
export function hasAllPermissions(
  permissions: string[],
  ...keys: string[]
): boolean {
  return keys.every((k) => permissions.includes(k));
}

/**
 * The roles the API treats as administrative — `req.user.role === OWNER || ADMIN`,
 * the predicate `isAdminRole(req)` applies server-side.
 *
 * Use this only where a gate genuinely mirrors a server-side ROLE check. Almost
 * everything in this product is keyed on a permission instead, and a role gate
 * added where a permission would do is a bug, not a shortcut.
 *
 * Note `web/src/components/timesheets/utils/reviewActions.ts` carries its own
 * copy. That module is pinned by a test requiring every one of its imports to be
 * `import type` — it must have no runtime coupling at all — so it cannot import
 * this. The two lists must be changed together; see docs/backlog/refactors.md.
 */
const ADMIN_ROLES: readonly string[] = ['OWNER', 'ADMIN'];

export const isAdminRole = (role: string | null | undefined): boolean =>
  !!role && ADMIN_ROLES.includes(role);
